import AsyncControl from '@/Controls/AsyncControl';
import ComponentManager from '@/Management/ComponentManager';
import TabStripTab2ViewModelType from '@/ViewModels/Controls/TabStripTab2ViewModel';
import LinkType from '@/Controls/Link';
import LayoutRenderer from '@/Utility/LayoutRenderer';
import * as CoreFrameDTOs from "coreframetypings/dto";
import ControlFactory from '@/Factories/ControlFactory';
import GroupFactory from '@/Factories/GroupFactory';
import ControlFactoryTuple from '@/Factories/ControlFactoryTuple';
import GroupFactoryTuple from '@/Factories/GroupFactoryTuple';
import Utilities from '@/Utility/Utilities';
import {DisposeBehavior} from '#/DefaultViewExtensionInterfaces';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';

export default class TabStripTab2 extends AsyncControl {
    private Link = ComponentManager.getControl<typeof LinkType>('Link');
    private jQueryControl: JQuery;
    private link: LinkType;

    public constructor(private viewModel: TabStripTab2ViewModelType, private element?: JQuery) {
        super(viewModel, element);
    }

    public async getElementAsync(): Promise<JQuery> {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div>');

        let title = $('<li class="c-title">').appendTo(this.jQueryControl);
        let content = $('<div class="c-content">').appendTo(this.jQueryControl);

        this.defineComputed(() => {
            if (this.isDisposed || this.viewModel.isDisposed)
                return;

            let isActive = this.viewModel.isActive();

            if (isActive) {
                content.addClass('animate');
                this.jQueryControl.prependTo(this.jQueryControl.parent());

                this.jQueryControl.slideDown(120, () => {
                    if (!Utilities.isNullOrUndefined(this.jQueryControl) && !this.jQueryControl[0].style.display)
                        this.jQueryControl[0].style.display = 'block';
                    content.removeClass('animate');
                });
            } else if (!isActive) {
                content.addClass('animate');

                this.jQueryControl.slideUp(120, () => {
                    if (!Utilities.isNullOrUndefined(this.jQueryControl) && !this.jQueryControl[0].style.display)
                        this.jQueryControl[0].style.display = 'none';
                    content.removeClass('animate');
                });
            }
        });

        const tuples: Array<ControlFactoryTuple | GroupFactoryTuple> = [];

        const createdItems = await this.createItemsAsync(this.viewModel.detailViewItems, this.createDetailViewItemAsync);
        if (this.isDisposed || this.viewModel.isDisposed)
            return null;

        for (const { tuple, controlFactory } of createdItems) {
            tuples.push(tuple);
            this.viewModel.__createdFactories.push(controlFactory);
        }

        this.viewModel.updateLabel();

        const createdGroups = await this.createItemsAsync(this.viewModel.detailViewGroups, this.createGroupAsync);
        if (this.isDisposed || this.viewModel.isDisposed)
            return null;

        for (const { tuple, groupFactory } of createdGroups) {
            tuples.push(tuple);
            this.viewModel.__createdFactories.push(groupFactory);
        }

        const $layout = await this.createItemAsync(tuples.filter(t => t != null), this.createLayout);
        if (this.isDisposed || this.viewModel.isDisposed)
            return null;

        content.append($layout);

        this.applyBindings(this.jQueryControl, {
            controlName: 'tabstriptab2',

            technicalName: this.viewModel.technicalName,

            loader: this.viewModel.isBusy,

            controlIdent: this.identifier,

            tooltip: this.viewModel.tooltip,

            invalid: this.viewModel.invalidationText
        });

        this.applyBindings(title, {
            technicalName: this.context.technicalName,

            locator: this.context.locator,

            states: this.definePureComputed(() => {
                let states = [];

                if (this.isDisposed || this.viewModel.isDisposed)
                    return states;

                if (this.viewModel.isActive() === true) {
                    states.push('active');
                }

                return states;
            }),

            visible: this.definePureComputed({
                read: () => {
                    if (this.context?.isDisposed !== false || !this.context.isVisible()) {
                        return false;
                    }

                    for (let i = 0; i < tuples.length; i++) {
                        if (tuples[i].factory?.isVisible()) {
                            return true;
                        }
                    }

                    this.context.isVisible(false);
                    return false;
                }
            }),

            attr: {
                role: 'tab',
                'aria-selected': this.defineComputed(() => {
                    if (this.isDisposed || this.viewModel.isDisposed)
                        return '';

                    return this.viewModel.isActive().toString();
                })
            },

            controls: content
        });

        this.applyBindings(content, {
            visible: this.viewModel.isVisible,
            attr: {
                role: 'tabpanel',
                'aria-hidden': this.defineComputed(() => {
                    if (this.isDisposed || this.viewModel.isDisposed)
                        return null;

                    return (!this.viewModel.isActive()).toString();
                })
            },
            labelledBy: title
        });

        if (this.viewModel.link != null) {
            this.link = new this.Link(this.viewModel.link);
            let tuple = this.link.getTuple();

            tuple.control.appendTo(title);
        }

        this.widget(null);

        return this.jQueryControl;
    }

    private async createDetailViewItemAsync(detailViewItem: CoreFrameDTOs.IDVGItemDTO) {
        return ControlFactory.createInstanceAsync(detailViewItem, this.viewModel.context, {
            locator: this.viewModel.locator,
            isDisplayed: this.createIsDisplayedComputed(),
            isSummaryViewMode: this.viewModel.isSummaryViewMode,
        });
    }

    protected createIsDisplayedComputed(): KnockoutComputed<boolean> {
        return this.defineComputed(() => {
            if (this.isDisposed || this.viewModel.isDisposed)
                return false;

            const isActive = this.viewModel.isActive();
            const isVisible = this.viewModel.isVisible();
            const isDisplayed = this.viewModel.isDisplayed();

            return isActive && isVisible && isDisplayed;
        });
    }

    private async createGroupAsync(detailViewGroup: CoreFrameDTOs.IDetailViewGroupDTO) {
        return GroupFactory.createInstanceAsync(detailViewGroup, this.viewModel.context, {
            locator: this.viewModel.locator,
            isDisplayed: this.createIsDisplayedComputed()
        });
    }

    private createLayout(tuples: Array<ControlFactoryTuple | GroupFactoryTuple>) {
        return LayoutRenderer.renderLayout(this.viewModel.layout, tuples, this.viewModel.context instanceof DetailViewContext ? this.viewModel.context.labelColumnWidth : null);
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }
}

ComponentManager.setControl('TabStripTab2', TabStripTab2, 'RADPresentation.HTMLClient.DefaultViewExtensions');